import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BookOpenText, Sparkle, ArrowRight } from "@phosphor-icons/react";
import { PageShell } from "../components/Layout";
import PlaceAutocompleteInput from "../components/PlaceAutocompleteInput";
import useBirthDetailsPrefill from "../hooks/useBirthDetailsPrefill";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));
const inputCls = "mt-1 block w-full min-w-0 rounded-xl border-2 border-[#E8A628]/50 focus:border-[#E8A628] outline-none bg-white px-3 py-2.5 text-[15px]";
const ORD = ["", "1st", "2nd", "3rd", "4th"];
const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");
const dms = (d) => `${Math.floor(d)}°${String(Math.round((d % 1) * 60)).padStart(2, "0")}′`;

const PlacementRow = ({ p, active, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full text-left rounded-2xl border px-4 py-3 flex items-center gap-3 transition-colors ${active ? "border-[#E8A628] ring-2 ring-[#E8A628]/50 bg-white" : "border-amber-200/70 bg-white hover:border-[#E8A628]/70"}`}
    data-testid={`kv-planet-${slug(p.name)}`}
  >
    <div className="w-24 shrink-0 font-semibold text-[#3F2D1B]">{p.name === "Lagna" ? "Ascendant" : p.name}{p.retrograde && p.name !== "Rahu" && p.name !== "Ketu" ? " (R)" : ""}</div>
    <div className="flex-1 min-w-0">
      <div className="text-[15px] text-[#3F2D1B]" data-testid={`kv-nak-${slug(p.name)}`}>{p.nakshatra} · pada {p.pada}</div>
      <div className="text-xs text-muted">{p.rashi} {dms(p.degrees_in_rashi)}</div>
    </div>
    <ArrowRight size={16} className="text-[#E8A628] shrink-0" />
  </button>
);

const Story = ({ p }) => (
  <article className="rounded-2xl bg-white border border-amber-200/70 p-5 sm:p-7" data-testid="kv-story">
    <div className="label-eyebrow text-muted">{p.name === "Lagna" ? "Ascendant (Lagna)" : p.name}</div>
    <h2 className="font-serif-display text-3xl mt-1 text-[#3F2D1B]" data-testid="kv-story-heading">{p.nakshatra} · {ORD[p.pada]} pada</h2>
    <p className="text-sm text-muted mt-2" data-testid="kv-story-where">
      Nakshatra #{p.nakshatra_number} (lord {p.nakshatra_lord}) · {p.name === "Lagna" ? "Ascendant" : p.name} at {p.rashi} {dms(p.degrees_in_rashi)} · this pada spans {p.pada_range[0]}°–{p.pada_range[1]}° of the zodiac
    </p>
    {p.sections.map((s) => (
      <section key={s.chapter} className="mt-6" data-testid={`kv-chapter-${s.chapter}`}>
        <div className="text-xs uppercase tracking-[0.18em] font-semibold text-[#B7791F]">Chapter {s.chapter}</div>
        <h3 className="font-serif-display text-xl text-[#3F2D1B] mt-1">{s.title}</h3>
        <div className="mt-3 space-y-2.5 text-[15px] leading-relaxed text-[#3F2D1B]/90">
          {s.text.map((t, i) => (
            <p key={i} className={/said:$/.test(t) ? "font-semibold italic text-[#7C5A2A]" : ""}>{t}</p>
          ))}
        </div>
      </section>
    ))}
    <p className="text-[11px] text-muted mt-6">From Karma Vipaka Samhita (English translation). A traditional text, not medical advice.</p>
  </article>
);

export default function AdminKarmaVipakaPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", date: "", hour: "", minute: "", place: "", timezone: "" });
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const storyRef = useRef(null);

  useEffect(() => {
    if (!authLoading && !user?.is_super_admin) navigate("/");
  }, [authLoading, user, navigate]);

  useBirthDetailsPrefill((bd, u) => {
    const [hh, mm] = String(bd?.time || "").split(":");
    setForm((f) => ({
      name: f.name || u?.name || "", date: f.date || bd?.date || "",
      hour: f.hour || hh || "", minute: f.minute || mm || "",
      place: f.place || bd?.place || "", timezone: f.timezone || bd?.timezone || "",
    }));
  });

  const run = async (e) => {
    e.preventDefault();
    if (!form.date || !form.hour || !form.minute || !form.place) return toast.error("Fill in date, time and place of birth.");
    if (!form.timezone) return toast.error("Pick the birth place from the suggestions list.");
    setBusy(true);
    try {
      const r = await api.post("/admin/karma-vipaka", { date: form.date, time: `${form.hour}:${form.minute}`, place: form.place, timezone: form.timezone });
      setData(r.data);
      setSel(null);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not compute the chart.");
    } finally {
      setBusy(false);
    }
  };

  const pick = (p) => {
    setSel(p);
    setTimeout(() => storyRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  if (authLoading || !user?.is_super_admin) return <PageShell><div className="p-10 text-center text-muted">Loading…</div></PageShell>;

  return (
    <PageShell>
      <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-20" data-testid="karma-vipaka-page">
        <div className="label-eyebrow text-muted flex items-center gap-2"><BookOpenText size={14} weight="duotone" /> Super-admin · Past-life karma by nakshatra pada</div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light leading-tight mt-3">Karma Vipaka <span className="text-gold">Samhita</span></h1>
        <p className="text-sm text-muted mt-3 max-w-3xl">Enter birth details to see the Ascendant and the 9 planets in their nakshatras and padas. Tap any one to read its story and expiation from the Karma Vipaka Samhita, exactly as written for that pada.</p>

        <form onSubmit={run} className="rounded-2xl bg-white border border-amber-200/70 p-5 sm:p-6 mt-8 grid sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_1.4fr_auto] gap-3 items-end" data-testid="kv-form">
          <label className="text-xs font-semibold text-[#3F2D1B]/70">Name (optional)<input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="e.g. Anita" data-testid="kv-name" /></label>
          <label className="text-xs font-semibold text-[#3F2D1B]/70">Date of birth<input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} data-testid="kv-date" /></label>
          <div className="text-xs font-semibold text-[#3F2D1B]/70">Time of birth (24-hr)
            <div className="flex gap-2">
              <select value={form.hour} onChange={(e) => setForm({ ...form, hour: e.target.value })} className={inputCls} data-testid="kv-hour"><option value="">HH</option>{HOURS.map((h) => <option key={h}>{h}</option>)}</select>
              <select value={form.minute} onChange={(e) => setForm({ ...form, minute: e.target.value })} className={inputCls} data-testid="kv-minute"><option value="">MM</option>{MINUTES.map((m) => <option key={m}>{m}</option>)}</select>
            </div>
          </div>
          <label className="text-xs font-semibold text-[#3F2D1B]/70">Place of birth
            <div className="mt-1"><PlaceAutocompleteInput value={form.place} onChange={(v) => setForm({ ...form, place: v, timezone: "" })} onPick={({ name, timezone }) => setForm((f) => ({ ...f, place: name, timezone }))} placeholder="Start typing your city…" testId="kv-place" /></div>
          </label>
          <button type="submit" disabled={busy} className="btn-primary rounded-full px-6 py-3 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="kv-submit">
            <Sparkle size={15} weight="fill" /> {busy ? "Reading…" : "Show karma"}
          </button>
        </form>

        {data && (
          <div className="mt-10 grid lg:grid-cols-[minmax(0,380px)_1fr] gap-6 items-start" data-testid="kv-results">
            <div className="space-y-2.5">
              <p className="text-xs text-muted" data-testid="kv-meta">{form.name ? `${form.name} · ` : ""}{data.birth_details.date} {data.birth_details.time} · {data.place_resolved} · {data.ayanamsa} ayanamsa</p>
              {data.placements.map((p) => <PlacementRow key={p.name} p={p} active={sel?.name === p.name} onClick={() => pick(p)} />)}
            </div>
            <div ref={storyRef} className="lg:sticky lg:top-24 scroll-mt-24">
              {sel ? <Story p={sel} /> : (
                <div className="rounded-2xl border border-dashed border-amber-300/70 p-8 text-center text-sm text-muted" data-testid="kv-story-empty">Tap the Ascendant or a planet to read its Karma Vipaka story.</div>
              )}
            </div>
          </div>
        )}
      </div>
    </PageShell>
  );
}
